/**
 * Vuetify theme palettes, derived from the official Mutual of Enumclaw brand
 * tokens in `@moe-tech/design`.
 *
 * Every value here is either a brand token verbatim or a tone-derivation of one
 * (same hue, adjusted lightness). The derivations exist because several brand
 * colors cannot carry accessible text at their published values — see
 * docs/color-selection.md, which records the measurements behind each choice and
 * is reproducible via `node scripts/color-evidence.mjs`.
 *
 * Two things here will look wrong if you don't know why:
 *
 *  1. Several light-theme pairs are below WCAG AA. That is a deliberate brand
 *     mandate, not an oversight — the brand guide puts white text on every
 *     palette colour except Sunshine, and the hexes are used unadjusted.
 *     The affected pairs are enumerated in scripts/color-evidence.mjs so the
 *     cost stays visible and any *further* degradation still fails the audit.
 *
 *  2. `warning`, `info`, `error` and `success` are FILL-ONLY in the light
 *     theme: they are the brand hex verbatim, so as text on white they read at
 *     1.85–4.33:1. Use the `*-text` roles for foreground. This does not apply
 *     to the dark theme, where every role sits at tone 80 and passes.
 *
 * Changing a value here without re-running the audit will silently break the
 * accessibility claim. `src/tests/unit/theme.test.ts` guards both.
 */

export type ThemePalette = Record<string, string>

/** The seven accent roles, in the order both palettes list them. */
const ROLES = ['primary', 'secondary', 'tertiary', 'error', 'warning', 'success', 'info'] as const

/**
 * Dark-theme containers.
 *
 * Every one of them is the same grey: dark mode does NOT tint a container with
 * its role colour (see the note in `darkColors`). Writing that rule out as
 * fourteen identical literals stated it seven times over and made it look like a
 * coincidence rather than the invariant it is. Deriving them means a new role in
 * ROLES gets its container for free, and a role that needs to break the rule has
 * to say so explicitly by overriding it below.
 */
const DARK_CONTAINER = '#303030'
const DARK_ON_CONTAINER = '#e2e2e2'
const darkContainers: ThemePalette = Object.fromEntries(
	ROLES.flatMap(role => [
		[`${role}-container`, DARK_CONTAINER],
		[`on-${role}-container`, DARK_ON_CONTAINER],
	]),
)

export const lightColors: ThemePalette = {
	background: '#f1f2f2',
	'on-background': '#2d2927',
	surface: '#ffffff',
	'on-surface': '#2d2927',
	// Vuetify paints a v-toolbar with `surface-light`, not `surface` — see
	// VToolbar/_variables.scss `$toolbar-background`. This used to equal
	// `background`, so toolbars were invisible in light mode while rendering
	// correctly in dark. It must differ from the page colour.
	'surface-bright': '#ffffff',
	'surface-light': '#ffffff',
	'on-surface-bright': '#2d2927',
	'on-surface-light': '#2d2927',
	'surface-variant': '#eeeeee',
	'on-surface-variant': '#474747',
	outline: '#777777',
	'outline-variant': '#c6c6c6',

	// BRAND MANDATE: the brand guide shows white text on every palette colour
	// except Sunshine, and the hexes are used with no tonal adjustment. Six
	// light-theme pairs therefore sit below WCAG AA — recorded, not accidental.
	// See BRAND_MANDATE in scripts/color-evidence.mjs.
	primary: '#144835',
	'on-primary': '#ffffff',
	'primary-container': '#448843',
	'on-primary-container': '#ffffff',

	// Base roles stay darkened so a button remains visible on its container;
	// only the containers carry the brand hex.
	secondary: '#486800',
	'on-secondary': '#ffffff',
	'secondary-container': '#93ca15',
	'on-secondary-container': '#ffffff',
	tertiary: '#006876',
	'on-tertiary': '#ffffff',
	'tertiary-container': '#3fcce3',
	'on-tertiary-container': '#ffffff',
	error: '#de7552',
	'on-error': '#ffffff',
	'error-container': '#ffe2d8',
	'on-error-container': '#331200',
	'error-text': '#a04022',
	warning: '#d6882b',
	'on-warning': '#ffffff',
	'warning-container': '#ffe3cb',
	'on-warning-container': '#291800',
	'warning-text': '#8a5100',
	success: '#448843',
	'on-success': '#ffffff',
	'success-container': '#b3faac',
	'on-success-container': '#072100',
	'success-text': '#266c29',

	// Sunshine is the brand guide's one exception: dark text.
	info: '#f0b52b',
	'on-info': '#2d2927',
	'info-container': '#ffe5bc',
	'on-info-container': '#251a00',
	'info-text': '#7a5900',
	focus: '#007685',

	
	'button-default': '#474747',
	'on-button-default': '#ffffff',
}

export const darkColors: ThemePalette = {
	...darkContainers,
	// Pure neutral grey, no brand tint. background and surface differ so cards
	// lift off the page.
	background: '#131313',
	'on-background': '#e2e2e2',
	surface: '#1f1f1f',
	'on-surface': '#e2e2e2',
	'surface-bright': '#393939',
	'surface-light': '#2a2a2a',
	'on-surface-bright': '#e2e2e2',
	'on-surface-light': '#e2e2e2',
	'surface-variant': '#474747',
	'on-surface-variant': '#c6c6c6',
	outline: '#919191',
	'outline-variant': '#474747',

	// Dark mode keeps the CONTAINERS GREY rather than tinting them with their
	// brand colour — colour arrives through the action on the card, which keeps
	// large dark surfaces calm and is how this app looked before the migration.
	primary: '#9cd2ba',
	'on-primary': '#003826',
	secondary: '#a1d82a',
	'on-secondary': '#253500',
	tertiary: '#4fd7ef',
	'on-tertiary': '#00363e',
	// Status roles use the brand hex VERBATIM here, with brand-black text.
	// Unlike the light theme they need no tonal lift: each already sits well
	// above the dark surface (5.3-8.9:1 as text on it), so the tone-80
	// derivation only made them paler. No fill-only split is needed either.
	//
	error: '#de7552',
	'on-error': '#2d2927',
	'error-text': '#de7552',
	warning: '#d6882b',
	'on-warning': '#2d2927',
	'warning-text': '#d6882b',
	// success is Alpine Green verbatim, matching light. This is the palette's
	// tonal dead zone and it costs twice: white text on it is 4.33:1 and black
	// is 3.33:1, so neither passes AA. It is also only 3.81:1 as text on the
	// dark surface, which is why `success-text` is lifted — fill and text are
	// deliberately different colours here.
	success: '#448843',
	'on-success': '#ffffff',
	'success-text': '#5ca059',
	info: '#f0b52b',
	'on-info': '#2d2927',
	'info-text': '#f0b52b',
	focus: '#4fd7ef',
	'button-default': '#393939',
	'on-button-default': '#e2e2e2',
}

/**
 * Vuetify's non-colour theme variables.
 *
 * These are NOT optional. Vuetify ships them with its built-in `light` and
 * `dark` themes, but a theme declared with only `colors` replaces that entry
 * wholesale and they are never emitted — leaving 21 rules in Vuetify's own
 * stylesheet referencing custom properties that do not exist.
 *
 * The failures are silent and look like unrelated bugs:
 *
 *   --v-high-emphasis-opacity   every `color-mix(… calc(var(…) * 100%) …)` text
 *                               rule is invalid, so the declaration is dropped
 *                               and the element INHERITS its colour instead —
 *                               e.g. a default button on a coloured card
 *                               rendering white-on-white.
 *   --v-shadow-color            `box-shadow: … rgba(var(--v-shadow-color), …)`
 *                               is invalid, so elevation disappears app-wide and
 *                               toolbars/cards stop reading as raised surfaces.
 *   --v-border-color/-opacity   dividers and outlined components lose borders.
 *   --v-*-opacity               hover, focus, disabled and selected states.
 *
 * Values are Vuetify's defaults, with `shadow-color` set to the brand black so
 * elevation matches the `--moe-shadow-*` tokens.
 *
 * `src/tests/unit/theme.test.ts` asserts these stay present.
 */
const REQUIRED_VARIABLES = {
	'border-opacity': 0.12,
	'shadow-color': '#2d2927',
	'medium-emphasis-opacity': 0.60,
	'idle-opacity': 0.04,
	'hover-opacity': 0.04,
	'focus-opacity': 0.12,
	'selected-opacity': 0.08,
	'activated-opacity': 0.12,
	'dragged-opacity': 0.08,
	'theme-on-dark': '#ffffff',
	'theme-on-light': '#000000',
	'elevation-overlay-opacity-step': '2%',
	'highlight-opacity': '8%',
}

export const lightVariables = {
	...REQUIRED_VARIABLES,
	'border-color': '#2d2927',
	'high-emphasis-opacity': 0.87,
	'disabled-opacity': 0.38,
	'pressed-opacity': 0.12,
	'theme-kbd': '#eeeeee',
	'theme-on-kbd': '#000000',
	'theme-code': '#f5f5f5',
	'theme-on-code': '#000000',
	'elevation-overlay-color': 'black',
}

export const darkVariables = {
	...REQUIRED_VARIABLES,
	'border-color': '#ffffff',
	'high-emphasis-opacity': 1,
	'disabled-opacity': 0.50,
	'idle-opacity': 0.10,
	'pressed-opacity': 0.16,
	'theme-kbd': '#424242',
	'theme-on-kbd': '#ffffff',
	'theme-code': '#343434',
	'theme-on-code': '#cccccc',
	'elevation-overlay-color': 'white',
}

/** Vuetify `theme` option, ready to spread into `createVuetify`. */
export function buildThemes(defaultTheme: string) {
	return {
		defaultTheme,
		themes: {
			light: { dark: false, colors: lightColors, variables: lightVariables },
			dark: { dark: true, colors: darkColors, variables: darkVariables },
		},
	}
}
